// Bucket re-entry for stop removal. When a stop's DISCARD mappings are
// deleted, the affected orders must re-enter the root pickups/dropoffs
// buckets of QUERY_PLANNING_DATA without a whole-board refetch — but only
// when the cache can prove it holds a fragment-complete bucket row; otherwise
// the caller falls back to the refetch. The eligibility predicate here is a
// client-side mirror of the planning query's bucket where-clauses
// (./queries.js, QUERY_PLANNING_DATA pickups/dropoffs) plus the
// date/ownership variables built in ../context.js — any change to either side
// must be mirrored here or bucket rows will mis-show/hide until the next full
// fetch.

import { gql } from '@apollo/client';
import { groupBy } from 'lodash';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { ORDER_FIELDS, ORDER_ROUTE_FIELDS } from '@/graphql/queries/orders';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';
import { SURVEY_QUESTION_FIELDS, SURVEY_RESPONSE_FIELDS } from '@/graphql/fragments/surveys';
import { removeRefs } from '@/graphql/util';
import { QUERY_PLANNING_DATA } from './queries';

// Composed from the SAME fragment constants QUERY_PLANNING_DATA's bucket
// selections use, so this fragment cannot drift from the query: a row that
// reads complete through it is safe to append to pickups/dropoffs. items and
// returns are @client read-function-backed off itemsByOrderId, so they never
// block the read; survey_responses is the field route-loaded orders lack.
export const BUCKET_ORDER_FRAGMENT = gql`
    ${ORDER_FIELDS}
    ${ORDER_ROUTE_FIELDS}
    ${ITEM_FIELDS}
    ${SURVEY_RESPONSE_FIELDS}
    ${SURVEY_QUESTION_FIELDS}

    fragment BucketOrderFields on orders {
        ...OrderFields
        ...OrderRouteFields
        itemsByOrderId {
            ...ItemFields
        }
        items @client {
            ...ItemFields
        }
        returns @client {
            ...ItemFields
        }
        services {
            service_id
            service_time
            quantity
        }
        survey_responses {
            ...SurveyResponseFields
            survey_question {
                ...SurveyQuestionFields
            }
        }
    }
`;

export const BUCKET_ORDER_STATUSES = ['pending', 'claimed', 'routed', 'routedActive'];
export const BUCKET_EXCLUDED_ORDER_TYPES = ['wh_inventory', 'will_call', 'wh_return'];

// An order is excluded from a bucket while it has an active DISCARD mapping
// of these types — mirrors the query's `_not: { routes: ... }` clauses and the
// assign direction in ../map/assignToRouteHelpers.js (patchPlanningData).
export const BUCKET_BLOCKING_MAPPING_TYPES = {
    pickups: ['PICKUP', 'FULL'],
    dropoffs: ['DROPOFF', 'FULL'],
};

export const ELIGIBILITY = {
    ELIGIBLE: 'ELIGIBLE',
    INELIGIBLE: 'INELIGIBLE',
    UNRESOLVABLE: 'UNRESOLVABLE',
};

/**
 * Evaluate a Hasura bool_exp of the exact shape ../context.js builds for
 * $orderCondition / $pickupCondition / $dropoffCondition: nested _or/_and over
 * per-attribute { _eq | _gte | _lt | _is_null } comparisons. Unknown operators
 * fail closed — the order is simply not re-added, matching what a server
 * refetch does with a non-matching row.
 *
 * @param {object} order - order row (post-update field values)
 * @param {object} condition - orders_bool_exp from the planning variables
 * @returns {boolean}
 */
export const matchesOrderCondition = (order, condition) => {
    return Object.entries(condition || {}).every(([key, value]) => {
        if (key === '_or') {
            return (value || []).some((clause) => matchesOrderCondition(order, clause));
        }
        if (key === '_and') {
            return (value || []).every((clause) => matchesOrderCondition(order, clause));
        }

        const attr = order?.[key];
        return Object.entries(value || {}).every(([op, operand]) => {
            switch (op) {
                case '_eq':
                    return attr === operand;
                case '_is_null':
                    return (attr == null) === operand;
                case '_gte':
                    return attr != null && new Date(attr) >= new Date(operand);
                case '_lt':
                    return attr != null && new Date(attr) < new Date(operand);
                default:
                    return false;
            }
        });
    });
};

/**
 * Pure eligibility predicate for one bucket, mirroring the planning query's
 * where-clause evaluated against the POST-update order row:
 *   - order_status in BUCKET_ORDER_STATUSES
 *   - order_type not in BUCKET_EXCLUDED_ORDER_TYPES
 *   - ownership per variables.orderCondition
 *   - leg date per variables.pickupCondition / dropoffCondition — a nulled
 *     date (sendBackToUnscheduled) matches ONLY when the unscheduled
 *     `_is_null` disjunct is present (plan-page-unscheduled circle)
 *   - pickups additionally require a wh_event with transition.next
 *     CD_PENDING_PO
 *   - no remaining active DISCARD mapping of the bucket's blocking types; the
 *     slim order.routes list lacks duplicate_override, so remaining blocking-
 *     type mappings are resolved via `resolveMapping` — an unresolvable one
 *     means the cache cannot prove eligibility (UNRESOLVABLE, caller falls
 *     back to refetch instead of guessing)
 *
 * @param {object} order - fragment-complete bucket row (post-update)
 * @param {object} args
 * @param {'pickups'|'dropoffs'} args.bucket
 * @param {object} args.variables - QUERY_PLANNING_DATA variables
 * @param {function} args.resolveMapping - slim mapping -> full mapping (with duplicate_override) or null
 * @returns {string} one of ELIGIBILITY
 */
export const bucketEligibility = (order, { bucket, variables, resolveMapping }) => {
    if (!BUCKET_ORDER_STATUSES.includes(order?.order_status)) {
        return ELIGIBILITY.INELIGIBLE;
    }
    if (BUCKET_EXCLUDED_ORDER_TYPES.includes(order?.order_type)) {
        return ELIGIBILITY.INELIGIBLE;
    }
    if (!matchesOrderCondition(order, variables?.orderCondition)) {
        return ELIGIBILITY.INELIGIBLE;
    }

    const legCondition = bucket === 'pickups' ? variables?.pickupCondition : variables?.dropoffCondition;
    if (!matchesOrderCondition(order, legCondition)) {
        return ELIGIBILITY.INELIGIBLE;
    }

    if (bucket === 'pickups' && !(order?.wh_events || []).some((e) => e?.transition?.next === 'CD_PENDING_PO')) {
        return ELIGIBILITY.INELIGIBLE;
    }

    const blockingTypes = BUCKET_BLOCKING_MAPPING_TYPES[bucket];
    for (const mapping of order?.routes || []) {
        if (!blockingTypes.includes(mapping?.type)) {
            continue;
        }
        const resolved = resolveMapping(mapping);
        if (!resolved) {
            return ELIGIBILITY.UNRESOLVABLE;
        }
        if (resolved.duplicate_override === 'DISCARD') {
            return ELIGIBILITY.INELIGIBLE;
        }
    }

    return ELIGIBILITY.ELIGIBLE;
};

/**
 * Derive per-bucket candidate order_ids from the deleted mappings — the
 * inverse of the assign direction: deleting a DISCARD mapping of type
 * PICKUP|FULL frees the order for the pickups bucket, DROPOFF|FULL for
 * dropoffs. Non-DISCARD mappings never held the order out of a bucket, so
 * their deletion is not a re-entry.
 *
 * @param {Array<object>} deletedMappings - delete_order_route_mappings.returning
 * @returns {{ pickups: string[], dropoffs: string[] }}
 */
export const bucketCandidateIds = (deletedMappings) => {
    const pickups = new Set();
    const dropoffs = new Set();
    for (const mapping of deletedMappings || []) {
        if (mapping?.duplicate_override !== 'DISCARD') {
            continue;
        }
        if (mapping.type === 'PICKUP' || mapping.type === 'FULL') {
            pickups.add(mapping.order_id);
        }
        if (mapping.type === 'DROPOFF' || mapping.type === 'FULL') {
            dropoffs.add(mapping.order_id);
        }
    }
    return { pickups: [...pickups], dropoffs: [...dropoffs] };
};

// A row that cannot be read through the full bucket selection (missing
// fields — typically survey_responses on an order that only ever loaded
// through a route — or a @client read function tripping over a skeletal
// entity) is incomplete: return null so the caller falls back to the refetch.
export const readBucketOrder = (cache, order_id) => {
    try {
        return cache.readFragment({
            id: cache.identify({ __typename: 'orders', order_id }),
            fragment: BUCKET_ORDER_FRAGMENT,
            fragmentName: 'BucketOrderFields',
        });
    } catch (error) {
        return null;
    }
};

// Presence probe + survey_responses-only writeback fragments. The routes query
// (ROUTE_ORDER_FIELDS) selects every BUCKET_ORDER_FRAGMENT field EXCEPT
// survey_responses, so a route-loaded order reads one field short of complete.
const ORDER_PRESENCE_FRAGMENT = gql`
    fragment OrderPresence on orders {
        order_id
    }
`;

const ORDER_SURVEY_RESPONSES_FRAGMENT = gql`
    ${SURVEY_RESPONSE_FIELDS}
    ${SURVEY_QUESTION_FIELDS}

    fragment OrderSurveyResponses on orders {
        survey_responses {
            ...SurveyResponseFields
            survey_question {
                ...SurveyQuestionFields
            }
        }
    }
`;

// When a freed order reads incomplete ONLY because survey_responses was never
// loaded (the route-side path), default the field to []. A plannable order
// (pending/claimed/routed) has no delivery survey responses yet, so [] is the
// accurate value — and it lets the order re-bucket client-side instead of
// forcing a whole-board refetch. Guarded so we never mint a skeletal orders
// row (entity must already exist) and never clobber real survey data (only
// writes when the field is genuinely absent). Returns true when it wrote — i.e.
// a re-read is worth attempting; false when the row is uncached or incomplete
// for some OTHER reason (the caller keeps its refetch fallback).
const defaultAbsentSurveyResponses = (cache, order_id) => {
    const id = cache.identify({ __typename: 'orders', order_id });
    let present = false;
    try {
        present = !!cache.readFragment({ id, fragment: ORDER_PRESENCE_FRAGMENT });
    } catch (error) {
        present = false;
    }
    if (!present) {
        return false;
    }
    let hasSurveyResponses = false;
    try {
        hasSurveyResponses =
            cache.readFragment({
                id,
                fragment: ORDER_SURVEY_RESPONSES_FRAGMENT,
                fragmentName: 'OrderSurveyResponses',
            }) != null;
    } catch (error) {
        hasSurveyResponses = false;
    }
    if (hasSurveyResponses) {
        return false;
    }
    cache.writeFragment({
        id,
        fragment: ORDER_SURVEY_RESPONSES_FRAGMENT,
        fragmentName: 'OrderSurveyResponses',
        data: { __typename: 'orders', order_id, survey_responses: [] },
    });
    return true;
};

export const appendBucketRows = (bucketRows, rows) => {
    if (!rows.length) {
        return bucketRows;
    }
    const existingIds = new Set((bucketRows || []).map((o) => o.order_id));
    return [...(bucketRows || []), ...rows.filter((o) => !existingIds.has(o.order_id))];
};

/**
 * update() body for UPDATE_ROUTE_STOPS in the stop-removal flow. Runs inside
 * the mutation's cache transaction AFTER the document write, so
 * update_routes_by_pk's returning has already replaced the route's
 * stop/mapping lists AND update_orders_many's OrderDispatchScalars returning
 * has already normalized the freed orders' post-removal scalars (nulled
 * windows, flipped status/carrier ids) — the eligibility reads in step 3 see
 * server-confirmed values, not the requested `_set`. In order:
 *   1. evict every deleted stop entity (invalidates the argument-less
 *      stopsByRouteId storage keys other pages read)
 *   2. filter every removed order's own slim `routes` mapping list — before
 *      any read, so appended rows carry the post-delete list
 *   3. read each candidate order through BUCKET_ORDER_FRAGMENT and append the
 *      eligible rows to the pickups/dropoffs buckets
 *
 * Returns needsRefetch: true when any candidate is not fragment-complete, a
 * blocking mapping is unresolvable, or QUERY_PLANNING_DATA itself is not
 * readable under these variables — the caller must fire the whole-board
 * refetch as the deterministic fallback. No bucket write happens on that path
 * (a partial row would make the planning read incomplete and trigger a full
 * network refetch anyway); steps 1-2 always run.
 *
 * @param {object} cache - Apollo cache (from the mutation's update callback)
 * @param {object} args
 * @param {object} args.data - UPDATE_ROUTE_STOPS / UNASSIGN_ORDERS_UPDATE response data
 * @param {object} args.variables - active QUERY_PLANNING_DATA variables (PlanningContext state.variables)
 * @returns {boolean} needsRefetch
 */
export const patchPlanningDataOnUnassign = (cache, { data, variables }) => {
    // Object form is required: evict(stringId) is not an Apollo 3 signature —
    // the string spreads into the options object and the missing `id` falls
    // back to ROOT_QUERY, evicting every root query field wholesale.
    (data?.delete_stops?.returning || []).forEach((stop) => cache.evict({ id: cache.identify(stop) }));

    const deletedMappings = data?.delete_order_route_mappings?.returning || [];
    if (!deletedMappings.length) {
        return false;
    }

    for (const [order_id, mappings] of Object.entries(groupBy(deletedMappings, 'order_id'))) {
        cache.modify({
            id: cache.identify({ __typename: 'orders', order_id }),
            fields: {
                routes: (existing = [], { toReference }) => removeRefs(existing, mappings, { toReference }),
            },
        });
    }

    const candidates = bucketCandidateIds(deletedMappings);
    const candidateIds = [...new Set([...candidates.pickups, ...candidates.dropoffs])];
    if (!candidateIds.length) {
        return false;
    }

    const rowsById = {};
    for (const order_id of candidateIds) {
        let row = readBucketOrder(cache, order_id);
        if (!row && defaultAbsentSurveyResponses(cache, order_id)) {
            // Route-loaded order missing only survey_responses — defaulted to
            // [] above, so it now reads complete and re-buckets without a
            // whole-board refetch.
            row = readBucketOrder(cache, order_id);
        }
        if (!row) {
            return true;
        }
        rowsById[order_id] = row;
    }

    const resolveMapping = (mapping) =>
        cache.readFragment({
            id: cache.identify({ __typename: 'order_route_mappings', mapping_id: mapping.mapping_id }),
            fragment: ORDER_ROUTE_MAPPINGS,
        });

    const eligibleRows = { pickups: [], dropoffs: [] };
    for (const bucket of Object.keys(eligibleRows)) {
        for (const order_id of candidates[bucket]) {
            const eligibility = bucketEligibility(rowsById[order_id], { bucket, variables, resolveMapping });
            if (eligibility === ELIGIBILITY.UNRESOLVABLE) {
                return true;
            }
            if (eligibility === ELIGIBILITY.ELIGIBLE) {
                eligibleRows[bucket].push(rowsById[order_id]);
            }
        }
    }

    if (!eligibleRows.pickups.length && !eligibleRows.dropoffs.length) {
        return false;
    }

    // A null/partial read here means the board isn't cached under these
    // variables (e.g. evicted by another flow) — the eligible rows computed
    // above cannot be appended anywhere, so the caller must fall back to the
    // refetch instead of silently dropping them.
    let queryUnreadable = false;
    cache.updateQuery({ query: QUERY_PLANNING_DATA, variables }, (prev) => {
        if (!prev?.routes) {
            queryUnreadable = true;
            return prev;
        }
        return {
            ...prev,
            pickups: appendBucketRows(prev.pickups, eligibleRows.pickups),
            dropoffs: appendBucketRows(prev.dropoffs, eligibleRows.dropoffs),
        };
    });

    return queryUnreadable;
};
